import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const PublicForm = () => {
    const { publicLinkId } = useParams();
    const location = useLocation();
    const { api } = useAuth();
    const [form, setForm] = useState(null);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);

    // Get email from URL parameters
    const urlParams = new URLSearchParams(location.search);
    const emailFromUrl = urlParams.get('email');

    useEffect(() => {
        const fetchForm = async () => {
            try {
                const response = await api.get(`/api/public/forms/${publicLinkId}/`);
                setForm(response.data);
                
                // Auto-fill email if provided in URL
                if (emailFromUrl) {
                    setFormData(prev => ({
                        ...prev,
                        // Find email field and auto-fill it
                        ...(response.data.fields?.reduce((acc, field) => {
                            if (field.field_type === 'email') {
                                acc[field.id] = emailFromUrl;
                            }
                            return acc;
                        }, {}))
                    }));
                }
            } catch (err) {
                console.error("Form not found", err);
                setError("Form not found or has been disabled.");
            } finally {
                setLoading(false);
            }
        };
        fetchForm();
    }, [publicLinkId, api, emailFromUrl]);

    const handleChange = (fieldId, value) => {
        setFormData({
            ...formData,
            [fieldId]: value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        
        try {
            await api.post(`/api/public/forms/${publicLinkId}/submit/`, { 
                data: formData 
            });
            setSubmitted(true);
        } catch (error) {
            console.error("Failed to submit form", error);
            setError(error.response?.data?.message || "Failed to submit form. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const renderField = (field) => {
        const commonProps = {
            id: `field-${field.id}`,
            name: field.id,
            value: formData[field.id] || '',
            onChange: (e) => handleChange(field.id, e.target.value),
            required: field.is_required,
            placeholder: field.placeholder || '',
            className: "mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        };

        switch (field.field_type) {
            case 'text':
                return <input type="text" {...commonProps} />;
            
            case 'textarea':
                return <textarea {...commonProps} rows={3} />;
            
            case 'email':
                return <input type="email" {...commonProps} />;
            
            case 'number':
                return <input type="number" {...commonProps} />;
            
            case 'phone':
                return <input type="tel" {...commonProps} />;
            
            case 'date':
                return <input type="date" {...commonProps} />;
            
            case 'select':
                return (
                    <select {...commonProps}>
                        <option value="">{field.placeholder || 'Select an option'}</option>
                        {field.options?.map((option, index) => (
                            <option key={index} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                );
            
            case 'checkbox':
                return (
                    <label className="flex items-center mt-2">
                        <input
                            type="checkbox"
                            {...commonProps}
                            checked={formData[field.id] || false}
                            onChange={(e) => handleChange(field.id, e.target.checked)}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">{field.label}</span>
                    </label>
                );
            
            case 'radio':
                return (
                    <div className="mt-2">
                        {field.options?.map((option, index) => (
                            <label key={index} className="flex items-center mt-2">
                                <input
                                    type="radio"
                                    name={field.id}
                                    value={option.value}
                                    checked={formData[field.id] === option.value}
                                    onChange={(e) => handleChange(field.id, e.target.value)}
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                                />
                                <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                            </label>
                        ))}
                    </div>
                );
            
            default:
                return <input type="text" {...commonProps} />;
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading form...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
                    <h2 className="text-2xl font-bold text-red-600 mb-4">Form Not Available</h2>
                    <p className="text-gray-600">{error}</p>
                </div>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
                    <div className="text-green-600 text-6xl mb-4">✓</div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Form Submitted Successfully!</h2>
                    <p className="text-gray-600">Thank you for your submission.</p>
                </div>
            </div>
        );
    }

    if (!form) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
                    <h2 className="text-2xl font-bold text-red-600 mb-4">Form Not Found</h2>
                    <p className="text-gray-600">The requested form could not be found.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 py-8">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                    <div className="px-6 py-8">
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">{form.title}</h1>
                        {form.description && (
                            <p className="text-gray-600 mb-8">{form.description}</p>
                        )}
                        
                        {emailFromUrl && (
                            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
                                <p className="text-sm text-blue-800">
                                    <strong>Note:</strong> Your email ({emailFromUrl}) has been automatically filled in the form.
                                </p>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            {form.fields?.map((field) => (
                                <div key={field.id}>
                                    <label htmlFor={`field-${field.id}`} className="block text-sm font-medium text-gray-700">
                                        {field.label}
                                        {field.is_required && <span className="text-red-500 ml-1">*</span>}
                                    </label>
                                    {renderField(field)}
                                    {field.help_text && (
                                        <p className="mt-1 text-sm text-gray-500">{field.help_text}</p>
                                    )}
                                </div>
                            ))}
                            
                            <div className="pt-6">
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="w-full bg-indigo-600 text-white py-3 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {submitting ? 'Submitting...' : 'Submit Form'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PublicForm;